/**
 * Yoink Adventures V2 - Geography Map Visualization
 *
 * Choropleth maps showing visited countries and US states.
 * Uses inline SVG for dependency-free rendering.
 *
 * Features:
 * - World map with country highlighting
 * - USA map with state highlighting
 * - Hover tooltips with visit counts
 * - Toggle between world and USA views
 * - Vintage travel theme styling
 */

import * as logger from './logger.js';
import { escapeHtml } from './utils.js';

// =============================================================================
// CONSTANTS
// =============================================================================

const COLORS = {
  visited: '#2A7B7B',      // vintage-teal for visited
  unvisited: '#e8e0d0',    // muted parchment for unvisited
  hover: '#D4A017',        // vintage-mustard for hover
  stroke: '#c4b8a4',       // subtle border
  water: '#d4e5f7',        // light blue for water/background
  text: '#1F2937'          // dark text
};

// USA state name to abbreviation mapping
const STATE_ABBREV = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
  'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
  'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
  'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
  'District of Columbia': 'DC'
};

// Reverse mapping: abbreviation to full name
const STATE_NAMES = Object.fromEntries(
  Object.entries(STATE_ABBREV).map(([name, abbrev]) => [abbrev, name])
);

// =============================================================================
// SVG MAP SOURCES
// =============================================================================

// World map SVG - local first, then CDN fallback
const WORLD_MAP_URLS = [
  '/wp-content/themes/astra/assets/maps/world.svg',  // Local (most reliable)
  '/adventure/maps/world.svg',                        // v2 subdomain copy
  'https://cdn.jsdelivr.net/gh/flekschas/simple-world-map@master/world-map.min.svg'
];

// USA states map - local only (no reliable CDN for this SVG)
const USA_MAP_URLS = [
  '/wp-content/themes/astra/assets/maps/usa-states.svg',  // Local
  '/adventure/maps/usa-states.svg'                         // v2 subdomain copy
];

// Cached SVG content
let worldMapSvg = null;
let usaMapSvg = null;

// =============================================================================
// SVG LOADING
// =============================================================================

/**
 * Fetch SVG from URL with timeout
 * @param {string} url - URL to fetch
 * @param {number} timeout - Timeout in ms
 * @returns {Promise<string|null>} SVG content or null
 */
async function fetchSvgWithTimeout(url, timeout = 5000) {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return await response.text();
  } catch (err) {
    if (err.name === 'AbortError') {
      logger.warn('[GeographyMap] Fetch timeout:', url);
    } else {
      logger.warn('[GeographyMap] Fetch failed:', url, err.message);
    }
    return null;
  }
}

/**
 * Fetch SVG with fallback URLs
 * @param {string[]} urls - Array of URLs to try in order
 * @returns {Promise<string|null>} SVG content or null
 */
async function fetchSvgWithFallback(urls) {
  for (const url of urls) {
    const svg = await fetchSvgWithTimeout(url);
    if (svg) return svg;
  }
  return null;
}

/**
 * Load world map SVG (cached)
 */
async function loadWorldMap() {
  if (!worldMapSvg) {
    worldMapSvg = await fetchSvgWithFallback(WORLD_MAP_URLS);
    // If external sources fail, use embedded fallback
    if (!worldMapSvg) {
      worldMapSvg = getEmbeddedWorldMap();
    }
  }
  return worldMapSvg;
}

/**
 * Load USA map SVG (cached)
 */
async function loadUsaMap() {
  if (!usaMapSvg) {
    usaMapSvg = await fetchSvgWithFallback(USA_MAP_URLS);
    // If external sources fail, use embedded fallback
    if (!usaMapSvg) {
      usaMapSvg = getEmbeddedUsaMap();
    }
  }
  return usaMapSvg;
}

/**
 * Get embedded world map SVG (fallback)
 * Simplified map with major countries - loads from separate data file
 */
function getEmbeddedWorldMap() {
  // Import from map-data module if available
  if (typeof window.YOINK_WORLD_MAP_SVG !== 'undefined') {
    return window.YOINK_WORLD_MAP_SVG;
  }
  return null;
}

/**
 * Get embedded USA map SVG (fallback)
 * Full 50 states map - loads from separate data file
 */
function getEmbeddedUsaMap() {
  // Import from map-data module if available
  if (typeof window.YOINK_USA_MAP_SVG !== 'undefined') {
    return window.YOINK_USA_MAP_SVG;
  }
  return null;
}

// =============================================================================
// DATA TRANSFORMATION
// =============================================================================

// Fallback mapping for country names to ISO codes (for locations without country_code)
const COUNTRY_NAME_TO_CODE = {
  'United States': 'us',
  'United Kingdom': 'gb',
  'Mexico': 'mx',
  'Canada': 'ca',
  'Spain': 'es',
  'France': 'fr',
  'Germany': 'de',
  'Italy': 'it',
  'Japan': 'jp',
  'Australia': 'au',
  'Brazil': 'br',
  'China': 'cn',
  'India': 'in',
  'Russia': 'ru',
  'Ireland': 'ie',
  'Netherlands': 'nl',
  'Portugal': 'pt',
  'Greece': 'gr',
  'Switzerland': 'ch',
  'Austria': 'at',
  'Belgium': 'be',
  'Poland': 'pl',
  'Sweden': 'se',
  'Norway': 'no',
  'Denmark': 'dk',
  'Finland': 'fi',
  'Colombia': 'co',
  'Panama': 'pa',
  'Costa Rica': 'cr',
  'French Polynesia': 'pf',
  'Puerto Rico': 'pr',
  'Bahamas': 'bs',
  'The Bahamas': 'bs'
};

/**
 * Extract visited country codes from location stats
 * @param {object} locationStats - Stats from locationService.calculateLocationStats
 * @returns {Set<string>} Set of lowercase ISO country codes
 */
export function getVisitedCountryCodes(locationStats) {
  const codes = new Set();
  if (!locationStats?.byCountry) return codes;

  for (const [countryName, countryData] of locationStats.byCountry) {
    if (countryData.countryCode) {
      codes.add(countryData.countryCode.toLowerCase());
    } else if (COUNTRY_NAME_TO_CODE[countryName]) {
      // Fallback to name-based lookup if countryCode is missing
      codes.add(COUNTRY_NAME_TO_CODE[countryName]);
    }
  }
  return codes;
}

/**
 * Extract visited US state abbreviations from location stats
 * @param {object} locationStats - Stats from locationService.calculateLocationStats
 * @returns {Set<string>} Set of uppercase state abbreviations
 */
export function getVisitedStateAbbrevs(locationStats) {
  const abbrevs = new Set();
  if (!locationStats?.byCountry) return abbrevs;

  // Get US data specifically
  const usData = locationStats.byCountry.get('United States');
  if (!usData?.states) return abbrevs;

  for (const [stateName] of usData.states) {
    const abbrev = STATE_ABBREV[stateName];
    if (abbrev) {
      abbrevs.add(abbrev);
    }
  }
  return abbrevs;
}

/**
 * Get country name from location stats by code
 * @param {object} locationStats - Stats object
 * @param {string} code - Lowercase ISO country code
 * @returns {string|null} Country name or null
 */
function getCountryNameByCode(locationStats, code) {
  if (!locationStats?.byCountry) return null;
  for (const [countryName, countryData] of locationStats.byCountry) {
    if (countryData.countryCode?.toLowerCase() === code) {
      return countryName;
    }
  }
  return null;
}

/**
 * Get yoink count for a country
 * @param {object} locationStats - Stats object
 * @param {string} code - Lowercase ISO country code
 * @returns {number} Yoink count
 */
function getCountryYoinkCount(locationStats, code) {
  if (!locationStats?.byCountry) return 0;
  for (const [, countryData] of locationStats.byCountry) {
    if (countryData.countryCode?.toLowerCase() === code) {
      return countryData.yoinkCount || 0;
    }
  }
  return 0;
}

/**
 * Get yoink count for a US state
 * @param {object} locationStats - Stats object
 * @param {string} abbrev - Uppercase state abbreviation
 * @returns {number} Yoink count
 */
function getStateYoinkCount(locationStats, abbrev) {
  const usData = locationStats?.byCountry?.get('United States');
  if (!usData?.states) return 0;

  const stateName = STATE_NAMES[abbrev];
  if (!stateName) return 0;

  const stateData = usData.states.get(stateName);
  return stateData?.yoinkCount || 0;
}

// =============================================================================
// RENDERING
// =============================================================================

/**
 * Render world map with visited countries highlighted
 * @param {HTMLElement} container - Container element
 * @param {object} locationStats - Stats from locationService.calculateLocationStats
 * @param {object} options - Render options
 */
export async function renderWorldMap(container, locationStats, options = {}) {
  const svgContent = await loadWorldMap();

  if (!svgContent) {
    container.innerHTML = `
      <div class="geography-map-error">
        <p>Could not load world map</p>
      </div>
    `;
    return;
  }

  const visitedCodes = getVisitedCountryCodes(locationStats);
  const countryCount = locationStats?.countries?.size || 0;

  // Create wrapper with stats header
  container.innerHTML = `
    <div class="geography-map-container" data-map-type="world">
      <div class="geography-map-header">
        <span class="geography-map-title">🌍 World</span>
        <span class="geography-map-stat">${countryCount} ${countryCount === 1 ? 'country' : 'countries'} visited</span>
      </div>
      <div class="geography-map-svg-wrapper">
        ${svgContent}
      </div>
      <div class="geography-map-tooltip hidden"></div>
    </div>
  `;

  // Style the SVG
  const svg = container.querySelector('svg');
  if (svg) {
    styleSvgMap(svg, 'world');
    colorVisitedRegions(svg, visitedCodes, 'world');
    attachMapInteractions(container, svg, locationStats, 'world');
  }
}

/**
 * Render USA map with visited states highlighted
 * @param {HTMLElement} container - Container element
 * @param {object} locationStats - Stats from locationService.calculateLocationStats
 * @param {object} options - Render options
 */
export async function renderUsaMap(container, locationStats, options = {}) {
  const svgContent = await loadUsaMap();

  if (!svgContent) {
    container.innerHTML = `
      <div class="geography-map-error">
        <p>Could not load USA map</p>
      </div>
    `;
    return;
  }

  const visitedAbbrevs = getVisitedStateAbbrevs(locationStats);
  const usData = locationStats?.byCountry?.get('United States');
  const stateCount = usData?.states?.size || 0;

  // Create wrapper with stats header
  container.innerHTML = `
    <div class="geography-map-container" data-map-type="usa">
      <div class="geography-map-header">
        <span class="geography-map-title">🇺🇸 United States</span>
        <span class="geography-map-stat">${stateCount} ${stateCount === 1 ? 'state' : 'states'} visited</span>
      </div>
      <div class="geography-map-svg-wrapper">
        ${svgContent}
      </div>
      <div class="geography-map-tooltip hidden"></div>
    </div>
  `;

  // Style the SVG
  const svg = container.querySelector('svg');
  if (svg) {
    styleSvgMap(svg, 'usa');
    colorVisitedRegions(svg, visitedAbbrevs, 'usa');
    attachMapInteractions(container, svg, locationStats, 'usa');
  }
}

/**
 * Render both maps with toggle
 * @param {HTMLElement} container - Container element
 * @param {object} locationStats - Stats from locationService.calculateLocationStats
 * @param {object} [options] - { tabs: false } renders both maps stacked with
 *   no tab bar (V2 uses this — 2026-07-12 feedback: tabs were dead weight
 *   when both maps show; V1 keeps the default tabbed toggle)
 */
export async function renderGeographyMaps(container, locationStats, options = {}) {
  const showTabs = options.tabs !== false;

  // Determine if user has US data
  const usData = locationStats?.byCountry?.get('United States');
  const hasUsData = usData && usData.states && usData.states.size > 0;
  const hasMultipleCountries = (locationStats?.countries?.size || 0) > 1;

  // Default to USA if has US data, otherwise world
  const defaultView = hasUsData ? 'usa' : 'world';

  container.innerHTML = `
    <div class="geography-maps-wrapper">
      ${showTabs ? `
      <div class="geography-map-tabs">
        <button class="geography-map-tab ${defaultView === 'world' ? 'active' : ''}" data-view="world">
          🌍 World
        </button>
        <button class="geography-map-tab ${defaultView === 'usa' ? 'active' : ''}" data-view="usa">
          🇺🇸 USA
        </button>
      </div>` : ''}
      <div class="geography-map-view ${showTabs && defaultView !== 'world' ? 'hidden' : ''}" id="geography-world-view">
        <div class="geography-map-loading">Loading world map...</div>
      </div>
      <div class="geography-map-view ${showTabs && defaultView !== 'usa' ? 'hidden' : ''}" id="geography-usa-view">
        <div class="geography-map-loading">Loading USA map...</div>
      </div>
    </div>
  `;

  // Attach tab handlers
  const tabs = container.querySelectorAll('.geography-map-tab');
  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const view = tab.dataset.view;
      tabs.forEach(t => t.classList.toggle('active', t === tab));
      container.querySelector('#geography-world-view').classList.toggle('hidden', view !== 'world');
      container.querySelector('#geography-usa-view').classList.toggle('hidden', view !== 'usa');
    });
  });

  // Load maps in parallel
  const worldContainer = container.querySelector('#geography-world-view');
  const usaContainer = container.querySelector('#geography-usa-view');

  await Promise.all([
    renderWorldMap(worldContainer, locationStats),
    renderUsaMap(usaContainer, locationStats)
  ]);
}

// =============================================================================
// STYLING
// =============================================================================

/**
 * Apply base styles to SVG map
 * @param {SVGElement} svg - The SVG element
 * @param {string} mapType - 'world' or 'usa'
 */
function styleSvgMap(svg, mapType) {
  // Make SVG responsive
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', 'auto');
  svg.style.maxWidth = '100%';
  svg.style.display = 'block';

  // Style all path elements (regions)
  const paths = svg.querySelectorAll('path');
  paths.forEach(path => {
    path.style.fill = COLORS.unvisited;
    path.style.stroke = COLORS.stroke;
    path.style.strokeWidth = '0.5';
    path.style.transition = 'fill 0.2s ease';
    path.style.cursor = 'pointer';
  });

  // Handle background/water elements if present
  const backgrounds = svg.querySelectorAll('rect, .water, .ocean, .sea');
  backgrounds.forEach(bg => {
    bg.style.fill = COLORS.water;
  });
}

/**
 * Color visited regions
 * @param {SVGElement} svg - The SVG element
 * @param {Set<string>} visitedIds - Set of visited region IDs
 * @param {string} mapType - 'world' or 'usa'
 */
function colorVisitedRegions(svg, visitedIds, mapType) {
  // First, handle <g> (group) elements - some countries use groups containing multiple paths
  const groups = svg.querySelectorAll('g[id]');
  groups.forEach(group => {
    const id = group.id;
    const normalizedId = mapType === 'world' ? id.toLowerCase() : id.toUpperCase();

    if (visitedIds.has(normalizedId)) {
      // Color all paths inside this group
      group.querySelectorAll('path').forEach(path => {
        path.style.fill = COLORS.visited;
        path.setAttribute('data-visited', 'true');
      });
      group.setAttribute('data-visited', 'true');
    }
  });

  // Then handle direct path elements with IDs
  const paths = svg.querySelectorAll('path[id]');
  paths.forEach(path => {
    // Skip paths already handled by group coloring
    if (path.getAttribute('data-visited') === 'true') return;

    const id = path.id;
    if (!id) return;

    // Normalize ID for comparison
    const normalizedId = mapType === 'world'
      ? id.toLowerCase()
      : id.toUpperCase();

    // Check if visited (handle various ID formats)
    let isVisited = visitedIds.has(normalizedId);

    // For USA map, also check without common prefixes
    if (!isVisited && mapType === 'usa') {
      // Some SVGs use full state names as IDs
      const abbrev = STATE_ABBREV[id];
      if (abbrev) {
        isVisited = visitedIds.has(abbrev);
      }
    }

    if (isVisited) {
      path.style.fill = COLORS.visited;
      path.setAttribute('data-visited', 'true');
    }
  });
}

/**
 * Attach hover and click interactions
 * @param {HTMLElement} container - Container element
 * @param {SVGElement} svg - The SVG element
 * @param {object} locationStats - Location stats
 * @param {string} mapType - 'world' or 'usa'
 */
function attachMapInteractions(container, svg, locationStats, mapType) {
  const tooltip = container.querySelector('.geography-map-tooltip');
  const paths = svg.querySelectorAll('path');

  paths.forEach(path => {
    const id = path.id || path.getAttribute('data-id') || path.getAttribute('data-name');
    if (!id) return;

    // Mouse enter - show tooltip
    path.addEventListener('mouseenter', (e) => {
      const isVisited = path.getAttribute('data-visited') === 'true';

      // Get region name and yoink count
      let regionName, yoinkCount;
      if (mapType === 'world') {
        regionName = getCountryNameByCode(locationStats, id.toLowerCase()) || id.toUpperCase();
        yoinkCount = getCountryYoinkCount(locationStats, id.toLowerCase());
      } else {
        const abbrev = STATE_ABBREV[id] || id.toUpperCase();
        regionName = STATE_NAMES[abbrev] || id;
        yoinkCount = getStateYoinkCount(locationStats, abbrev);
      }

      // Highlight on hover
      if (!isVisited) {
        path.style.fill = COLORS.hover;
      }

      // Show tooltip
      if (isVisited) {
        tooltip.innerHTML = `
          <strong>${escapeHtml(regionName)}</strong>
          <span class="geography-tooltip-count">${yoinkCount} yoink${yoinkCount !== 1 ? 's' : ''}</span>
        `;
      } else {
        tooltip.innerHTML = `<span class="geography-tooltip-unvisited">${escapeHtml(regionName)}</span>`;
      }
      tooltip.classList.remove('hidden');
    });

    // Mouse move - position tooltip
    path.addEventListener('mousemove', (e) => {
      const rect = container.getBoundingClientRect();
      tooltip.style.left = `${e.clientX - rect.left + 10}px`;
      tooltip.style.top = `${e.clientY - rect.top - 30}px`;
    });

    // Mouse leave - hide tooltip and reset color
    path.addEventListener('mouseleave', () => {
      const isVisited = path.getAttribute('data-visited') === 'true';
      if (!isVisited) {
        path.style.fill = COLORS.unvisited;
      }
      tooltip.classList.add('hidden');
    });
  });
}

// =============================================================================
// CSS STYLES (injected once)
// =============================================================================

let stylesInjected = false;

/**
 * Inject CSS styles for geography maps
 */
export function injectGeographyMapStyles() {
  if (stylesInjected) return;
  stylesInjected = true;

  const styles = `
    .geography-maps-wrapper {
      background: #fdfbf7;
      border-radius: 12px;
      border: 1px solid #e8e0d0;
      overflow: hidden;
      margin-top: 16px;
    }

    /* Self-contained hiding: V1's global .hidden isn't in the V2
       stylesheet, so without these the inactive map never hid and the
       tooltip rendered as a stray dark pill (seen on-device 2026-07-12). */
    .geography-map-view.hidden,
    .geography-map-tooltip.hidden {
      display: none;
    }

    .geography-map-tabs {
      display: flex;
      border-bottom: 1px solid #e8e0d0;
      background: #f5f0e6;
    }

    .geography-map-tab {
      flex: 1;
      padding: 12px 16px;
      border: none;
      background: transparent;
      font-size: 14px;
      font-weight: 500;
      color: #9CA3AF;
      cursor: pointer;
      transition: all 0.2s;
      opacity: 0.7;
    }

    .geography-map-tab:hover {
      background: rgba(42, 123, 123, 0.1);
      color: #6B7280;
      opacity: 1;
    }

    .geography-map-tab.active {
      background: #fdfbf7;
      color: #2A7B7B;
      border-bottom: 3px solid #2A7B7B;
      margin-bottom: -1px;
      font-weight: 600;
      opacity: 1;
    }

    .geography-map-view {
      padding: 16px;
    }

    .geography-map-container {
      position: relative;
    }

    .geography-map-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      padding: 0 4px;
    }

    .geography-map-title {
      font-size: 16px;
      font-weight: 600;
      color: #1F2937;
    }

    .geography-map-stat {
      font-size: 14px;
      color: #2A7B7B;
      font-weight: 500;
    }

    .geography-map-svg-wrapper {
      background: ${COLORS.water};
      border-radius: 8px;
      padding: 12px;
      overflow: hidden;
    }

    .geography-map-svg-wrapper svg {
      display: block;
      width: 100%;
      height: auto;
    }

    .geography-map-tooltip {
      position: absolute;
      background: #1F2937;
      color: white;
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 13px;
      pointer-events: none;
      z-index: 100;
      white-space: nowrap;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    }

    .geography-tooltip-count {
      display: block;
      font-size: 11px;
      color: #9CA3AF;
      margin-top: 2px;
    }

    .geography-tooltip-unvisited {
      color: #9CA3AF;
    }

    .geography-map-loading {
      text-align: center;
      padding: 40px;
      color: #6B7280;
    }

    .geography-map-error {
      text-align: center;
      padding: 40px;
      color: #9CA3AF;
    }

    /* Legend */
    .geography-map-legend {
      display: flex;
      gap: 16px;
      justify-content: center;
      margin-top: 12px;
      font-size: 12px;
      color: #6B7280;
    }

    .geography-legend-item {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .geography-legend-swatch {
      width: 14px;
      height: 14px;
      border-radius: 3px;
      border: 1px solid #c4b8a4;
    }

    .geography-legend-swatch.visited {
      background: ${COLORS.visited};
    }

    .geography-legend-swatch.unvisited {
      background: ${COLORS.unvisited};
    }

    /* Mobile adjustments */
    @media (max-width: 640px) {
      .geography-map-tabs {
        font-size: 13px;
      }

      .geography-map-tab {
        padding: 10px 12px;
      }

      .geography-map-view {
        padding: 12px;
      }

      .geography-map-header {
        flex-direction: column;
        gap: 4px;
        align-items: flex-start;
      }
    }
  `;

  const styleEl = document.createElement('style');
  styleEl.textContent = styles;
  document.head.appendChild(styleEl);
}
